import { Description, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { FunctionComponent, useCallback } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  ButtonContainer,
  ChildrenWrapper,
  CrossBtn,
  DoneButton,
  ModalBackdrop,
  ModalContainer,
  StyledBigIcon,
  StyledContainer,
  StyledDescription,
  StyledTitle,
} from "./styles";

import ErrorModal from "./ErrorModal";

import CookiesModal from "./CookiesModal";

import BottomSheetModal from "./BottomSheetModal";

import OptinModal from "./OptinModal";

import Cross from "components/IconImage/cross";

import { configState, sendULFEvent, setModal } from "state/config-slice";
import { AppDispatch } from "state/reducers-combiner";
import { ULFEvents } from "types/ULF";
import { clearActivationSuccessModal } from "utils/activationSuccessModal";

const Modal: FunctionComponent = () => {
  const { t } = useTranslation();

  const currentConfig = useSelector(configState);
  const dispatch: AppDispatch = useDispatch();
  const modal = currentConfig && currentConfig.modal;
  const [cookies] = useCookies();

  const {
    icon,
    title,
    description,
    type,
    cross,
    children,
    size,
    customButtons,
    showModal = undefined,
    errorModal,
    cookieModal,
    textAlign,
    enableGradient = true,
    logInModal,
    optinModal,
  } = modal;

  const cancel = useCallback(() => {
    clearActivationSuccessModal();
    dispatch(setModal({ showModal: false }));

    if (cookies.SUBSCRIPTIONPLUS_TRACKING === "true") {
      dispatch(
        sendULFEvent({
          "event-name": ULFEvents.CANCEL_CLICKED,
          page: window.location.pathname,
          source: cookies.SOURCE,
          isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
        }),
      );
    }
  }, [cookies.SOURCE, cookies.SUBSCRIPTIONPLUS_TRACKING, dispatch]);

  if (showModal) {
    if (errorModal) {
      return <ErrorModal />;
    }

    if (cookieModal) {
      return <CookiesModal />;
    }

    if (logInModal) {
      return <BottomSheetModal />;
    }

    if (optinModal) {
      return <OptinModal />;
    }

    return (
      <Dialog open={modal.showModal} onClose={cancel} as={ModalContainer} data-testid="default-modal">
        <ModalBackdrop data-testid="modal-backdrop" />

        <DialogPanel as={StyledContainer} type={type} size={size}>
          {icon ? <StyledBigIcon data-testid="modal-icon">{icon}</StyledBigIcon> : null}

          {title && (
            <DialogTitle data-testid="trialOffer-t&cs-title" as={StyledTitle} $textAlign={textAlign}>
              {title}
            </DialogTitle>
          )}

          {description && (
            <Description data-testid="trialOffer-t&cs-description" as={StyledDescription} $textAlign={textAlign}>
              {description}
            </Description>
          )}

          {children && (
            <ChildrenWrapper $enableGradient={enableGradient} $textAlign={textAlign}>
              {children}
            </ChildrenWrapper>
          )}

          {cross && (
            <CrossBtn data-testid="modal-cross" onClick={cancel} aria-label={t("Close")} title={t("Close")}>
              <Cross />
            </CrossBtn>
          )}

          {customButtons ? (
            customButtons
          ) : (
            <ButtonContainer>
              <DoneButton
                title={t("Done")}
                data-testid={"done-btn"}
                onClick={() => {
                  clearActivationSuccessModal();
                  dispatch(setModal({ showModal: false }));
                }}
              ></DoneButton>
            </ButtonContainer>
          )}
        </DialogPanel>
      </Dialog>
    );
  }

  return null;
};

export default Modal;
